import type {
  ProponentBeneficiarioFormValues,
  SeguradoBeneficiarioFormValues,
} from '../types/vida/cadastralMaintenance/beneficiarioFormTypes';
import type {
  BeneficiaryInclusionItem,
  BeneficiaryUpdateItem,
} from '../types/vida/inclusionProponent/inclusionProponentType';
import type { SeguradoDetailBeneficiaryItem } from '../types/vida/maintenanceSegurado/maintenanceSeguradoType';
import { resolveSeguradoParentescoLabel } from './parentescoUtils';

export const NM_BENEFICIARIO_MAX_LENGTH = 50;
export const DS_CURSO_MAX_LENGTH = 40;
export const DS_SERIE_MAX_LENGTH = 20;

export const EMPTY_PROPONENT_BENEFICIARIO_FORM_VALUES: ProponentBeneficiarioFormValues = {
  idBeneficiario: '',
  nmBeneficiario: '',
  cdMatricula: '',
  cdParentesco: '',
  pcParticipacao: '',
  dsCurso: '',
  dsSerie: '',
  vrMensalidade: '',
};

export const EMPTY_SEGURADO_BENEFICIARIO_FORM_VALUES: SeguradoBeneficiarioFormValues = {
  localId: '',
  nVidaPrvioFatmt: undefined,
  codBneficiario: null,
  nomeBeneficiario: '',
  codTipoParentesco: '',
  percentualPart: '',
};

export const isProponentBeneficiarioFormValid = (
  values: ProponentBeneficiarioFormValues,
): boolean => (
  values.nmBeneficiario.trim() !== ''
  && values.cdParentesco.trim() !== ''
  && values.pcParticipacao.trim() !== ''
);

export const isSeguradoBeneficiarioFormValid = (
  values: SeguradoBeneficiarioFormValues,
): boolean => (
  values.nomeBeneficiario.trim() !== ''
  && values.codTipoParentesco.trim() !== ''
  && values.percentualPart.trim() !== ''
);

export const createEmptyProponentBeneficiarioFormValues = (
  matricula = '',
  nextId?: string,
): ProponentBeneficiarioFormValues => ({
  ...EMPTY_PROPONENT_BENEFICIARIO_FORM_VALUES,
  idBeneficiario: nextId ?? '',
  cdMatricula: matricula,
});

export const createEmptySeguradoBeneficiarioFormValues = (
  localId: string,
  nVidaPrvioFatmt?: number,
): SeguradoBeneficiarioFormValues => ({
  ...EMPTY_SEGURADO_BENEFICIARIO_FORM_VALUES,
  localId,
  nVidaPrvioFatmt,
});

export const mapProponentBeneficiarioFormToInclusionItem = (
  values: ProponentBeneficiarioFormValues,
  index: number,
): BeneficiaryInclusionItem => ({
  idBeneficiario: values.idBeneficiario.trim() || String(index + 1),
  nmBeneficiario: values.nmBeneficiario.trim(),
  cdMatricula: values.cdMatricula.trim(),
  cdParentesco: values.cdParentesco.trim(),
  pcParticipacao: values.pcParticipacao.trim(),
  dsCurso: values.dsCurso.trim(),
  dsSerie: values.dsSerie.trim(),
  vrMensalidade: values.vrMensalidade.trim(),
});

export const mapProponentBeneficiarioFormToUpdateItem = (
  values: ProponentBeneficiarioFormValues,
  index: number,
): BeneficiaryUpdateItem => ({
  idBeneficiario: values.idBeneficiario.trim() || String(index + 1),
  nmBeneficiario: values.nmBeneficiario.trim(),
  cdParentesco: values.cdParentesco.trim(),
  pcParticipacao: values.pcParticipacao.trim(),
  cdMatricula: values.cdMatricula.trim() || undefined,
  dsCurso: values.dsCurso.trim() || undefined,
  dsSerie: values.dsSerie.trim() || undefined,
  vrMensalidade: values.vrMensalidade.trim() || undefined,
});

export const mapSeguradoDetailBeneficiaryToFormValues = (
  item: SeguradoDetailBeneficiaryItem,
  index: number,
): SeguradoBeneficiarioFormValues => ({
  localId: `${item.codBneficiario ?? 'beneficiario'}-${item.codTipoParentesco ?? 'parentesco'}-${index}`,
  nVidaPrvioFatmt: item.nVidaPrvioFatmt,
  codBneficiario: item.codBneficiario ?? null,
  nomeBeneficiario: item.nomeBeneficiario?.trim() ?? '',
  codTipoParentesco: item.codTipoParentesco != null && item.codTipoParentesco !== ''
    ? String(item.codTipoParentesco)
    : '',
  percentualPart: String(item.percentualPart ?? item.percentual ?? ''),
});

export const mapSeguradoBeneficiarioFormToDetailItem = (
  values: SeguradoBeneficiarioFormValues,
): SeguradoDetailBeneficiaryItem => {
  const percentual = Number(values.percentualPart.replace(',', '.'));

  return {
    nVidaPrvioFatmt: values.nVidaPrvioFatmt,
    codBneficiario: values.codBneficiario,
    nomeBeneficiario: values.nomeBeneficiario.trim(),
    codTipoParentesco: values.codTipoParentesco.trim(),
    parentesco: resolveSeguradoParentescoLabel(values.codTipoParentesco),
    percentualPart: Number.isFinite(percentual) ? percentual : 0,
    percentual: Number.isFinite(percentual) ? percentual : 0,
  };
};
